<script setup lang="ts">
import { useRouter } from 'vue-router'
import { watch, ref, computed } from 'vue'
import { track } from '@/lib/usageTracker'
import EntityTaskBadge from '@/components/task/EntityTaskBadge.vue'
import { Alert, Badge, Button, Pagination, TabNav, SearchInput } from '@/components/ui'
import { CreditCard, RefreshCw, CheckCircle2, AlertTriangle, History, Link } from 'lucide-vue-next'
import { usePaymentsPage } from '@/composables/usePaymentsPage'
import { useEntityLinkStudio } from '@/composables/useEntityLinkStudio'
import EntityAssociationDialog from '@/components/entity/EntityAssociationDialog.vue'
import {
  type QboPaymentSearchResult,
  type PaymentAssociationRow,
  searchQboPaymentsPaged,
  getPaymentLink,
  postPaymentLink,
} from '@/api/lytAccountingClient'

// State, computed, and handlers are owned by the usePaymentsPage composable.
// The SFC consumes the returned refs/functions directly (D-17.26.PathB).
const {
  associationRows,
  associationPage,
  associationPageSize,
  associationTotal,
  syncingRow,
  syncingAllFeedback,
  syncingAllLoading,
  error,
  associationLoading,
  loadAssociations,
  syncSinglePayment,
  syncAllUnassociated,
  setAssociationStatus,
  setPageSize,
  formatDate,
} = usePaymentsPage()
const router = useRouter()

// --- Tab state (Phase 25) ---
const activeTab = ref<'lytning' | 'qbo'>('lytning')
const dialogOpen = ref(false)
const dialogSource = ref<'lytning' | 'qbo'>('lytning')
const dialogLytningEntity = ref<PaymentAssociationRow | null>(null)
const dialogQboEntity = ref<QboPaymentSearchResult | null>(null)

// --- QBO link studio (Phase 25) ---
const studio = useEntityLinkStudio<QboPaymentSearchResult>({
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  searchPredicate: searchQboPaymentsPaged as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  linkAction: (qboId, lytId) => postPaymentLink(qboId, lytId) as any,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  reverseLookupAction: (qboId, lytId) => getPaymentLink(qboId, lytId) as any,
  entityLabel: 'payment',
})

async function handleAssociated() {
  dialogOpen.value = false
  await loadAssociations()
}

function openLinkDialog(source: 'lytning' | 'qbo', entity?: PaymentAssociationRow | QboPaymentSearchResult) {
  if (source === 'lytning') {
    dialogLytningEntity.value = entity as PaymentAssociationRow | null
    dialogQboEntity.value = null
  } else {
    dialogQboEntity.value = entity as QboPaymentSearchResult | null
    dialogLytningEntity.value = null
  }
  dialogSource.value = source
  dialogOpen.value = true
}

// Single searchQuery drives both tabs: Lytning filters in-memory; QBO debounces to studio.
const searchQuery = ref('')
const sharedFilter = ref<'all' | 'associated' | 'unassociated' | 'syncing'>('all')
const filteredLytningRows = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return associationRows.value
  return associationRows.value.filter((row) => {
    const haystack = [
      row.lytsoftPaymentId,
      row.customerName,
      row.qboPaymentId,
      row.paymentType,
      row.paymentDate,
      ...(row.linkedInvoiceNumbers ?? []),
    ]
      .filter(Boolean)
      .map((s) => String(s).toLowerCase())
      .join(' ')
    return haystack.includes(q)
  })
})

function mapUiFilterToStudio(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  if (filter === 'associated') return 'linked' as const
  if (filter === 'unassociated') return 'unlinked' as const
  return filter
}

function onSharedFilterClick(filter: 'all' | 'associated' | 'unassociated' | 'syncing') {
  // Cancel any pending debounced search so the filter click
  // is the single source of truth for issuing a new search
  // (setAssociationFilter triggers searchQbo internally).
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer)
    searchDebounceTimer = null
  }
  track('btn:PaymentFilter', 'click')
  sharedFilter.value = filter
  if (activeTab.value === 'lytning') {
    void setAssociationStatus(filter)
  } else {
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(filter))
  }
}

// Replay the shared filter when the active tab changes so filter state persists.
watch(activeTab, (tab) => {
  if (tab === 'lytning') {
    void setAssociationStatus(sharedFilter.value)
  } else {
    // Sync studio.query BEFORE setAssociationFilter so the
    // composable's internal searchQbo picks up the typed query.
    // Cancel any pending debounced search to avoid duplicate requests.
    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer)
      searchDebounceTimer = null
    }
    studio.query.value = searchQuery.value
    studio.setAssociationFilter(mapUiFilterToStudio(sharedFilter.value))
  }
})

let searchDebounceTimer: ReturnType<typeof setTimeout> | null = null
watch(searchQuery, (q) => {
  if (activeTab.value === 'qbo') {
    if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
    searchDebounceTimer = setTimeout(() => {
      void studio.searchQbo(q, 1)
    }, 300)
  }
})
</script>

<template>
  <div class="app-page app-page--narrow">
    <section class="app-page-header">
      <div class="app-page-heading">
        <div class="app-page-eyebrow">
          <CreditCard class="h-4 w-4" />
          <span>Settlements</span>
        </div>
        <h1 class="app-page-title">Payment sync coverage</h1>
        <p class="app-page-subtitle">Track payment settlement synchronization with your accounting provider.</p>
        <div class="page-badge-strip">
          <EntityTaskBadge entityType="PAYMENT" />
          <Button variant="ghost" size="sm" @click="() => { track('btn:ViewPaymentSyncHistory', 'click'); router.push('/app/tasks?entityType=PAYMENT') }">
            <History class="h-3.5 w-3.5" />
            View sync history
          </Button>
        </div>
      </div>
      <div class="app-page-actions">
        <Button variant="secondary" :loading="syncingAllLoading" @click="() => { track('btn:SyncAllUnlinkedPayments', 'click'); syncAllUnassociated() }">
          <RefreshCw class="h-4 w-4" />
          {{ syncingAllLoading ? 'Working\u2026' : 'Sync all unlinked' }}
        </Button>
      </div>
    </section>

    <Alert v-if="error" variant="error" title="Payment sync issue" compact class="page-alert">
      {{ error }}
    </Alert>

    <Alert v-if="syncingAllFeedback" variant="info" title="Payment bulk sync" compact class="page-alert">
      {{ syncingAllFeedback }}
    </Alert>

    <EntityAssociationDialog
      :open="dialogOpen"
      entityType="payment"
      :source="dialogSource"
      :lytningEntity="dialogLytningEntity ?? undefined"
      :qboEntity="dialogQboEntity ?? undefined"
      @update:open="dialogOpen = $event"
      @associated="handleAssociated"
    />
    <section class="assoc-section">
      <!-- Shared search + filter row (above both tabs) -->
      <div class="entity-search">
        <SearchInput
          v-model="searchQuery"
          label="Search payments"
          placeholder="Search by id, payment #, customer"
          :loading="activeTab === 'qbo' && studio.loading.value"
        />
      </div>
      <div class="status-tabs" role="tablist" aria-label="Filter by association status">
        <button
          v-for="tab in ['all', 'associated', 'unassociated', 'syncing'] as const"
          :key="tab"
          type="button"
          role="tab"
          class="status-tab"
          :class="{
            'status-tab--active': sharedFilter === tab,
            'status-tab--disabled': tab === 'syncing' && activeTab === 'qbo',
          }"
          :disabled="tab === 'syncing' && activeTab === 'qbo'"
          :title="tab === 'syncing' && activeTab === 'qbo' ? 'Syncing is a Lytning concept — not available on the QBO tab' : undefined"
          :aria-selected="sharedFilter === tab"
          @click="onSharedFilterClick(tab)"
        >
          {{ tab.charAt(0).toUpperCase() + tab.slice(1) }}
        </button>
      </div>

      <!-- Main tabs (Phase 25) -->
      <TabNav
        v-model="activeTab"
        :tabs="[
          { id: 'lytning', label: 'Lytning Payments' },
          { id: 'qbo', label: 'QBO Payments' }
        ]"
      />
      <div v-if="activeTab === 'lytning'">
        <!-- Loading state -->
        <div v-if="associationLoading" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading associations...</span>
        </div>

        <!-- Empty state -->
        <div v-else-if="filteredLytningRows.length === 0" class="tasks-empty">
          <div class="tasks-empty-symbol">
            <CreditCard class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">{{ searchQuery ? 'No matches' : 'No payments found' }}</div>
          <div class="tasks-empty-copy">
            {{ searchQuery ? `No payments match "${searchQuery}". Try a different search term.` : 'Try a different filter or sync unlinked payments first.' }}
          </div>
        </div>

        <!-- Association table -->
        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>LytSoft ID</th>
                <th>Date</th>
                <th>Total</th>
                <th>Customer</th>
                <th>Type</th>
                <th>Status</th>
                <th>QBO Payment ID</th>
                <th>Synced At</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in filteredLytningRows" :key="payment.lytsoftPaymentId">
                <td class="td-mono">{{ payment.lytsoftPaymentId }}</td>
                <td>{{ formatDate(payment.paymentDate) }}</td>
                <td>{{ payment.currencySymbol ?? '' }}{{ payment.totalAmount != null ? payment.totalAmount.toFixed(2) : '\u2014' }}</td>
                <td>{{ payment.customerName ?? '\u2014' }}</td>
                <td>{{ payment.paymentType ?? '\u2014' }}</td>
                <td>
                  <Badge v-if="payment.qboPaymentId" variant="success">
                    <CheckCircle2 class="h-3 w-3" />
                    Associated
                  </Badge>
                  <Badge v-else variant="warning">
                    <AlertTriangle class="h-3 w-3" />
                    Unassociated
                  </Badge>
                </td>
                <td class="td-mono">{{ payment.qboPaymentId ?? '\u2014' }}</td>
                <td>{{ formatDate(payment.syncedAt) }}</td>
                <td>
                  <div class="action-cell">
                    <div v-if="payment.runningTaskId" class="sync-indicator">
                      <RefreshCw class="h-4 w-4 spin" />
                      Syncing #{{ payment.runningTaskId }}
                    </div>
                    <div v-else class="action-buttons">
                      <Button
                        variant="outline"
                        size="sm"
                        @click="() => { track('btn:SyncPayment', 'click'); syncSinglePayment(payment) }"
                        :loading="syncingRow === payment.lytsoftPaymentId"
                      >
                        <RefreshCw class="h-4 w-4" />
                        Sync
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        @click="() => { track('btn:LinkPaymentLytning', 'click'); openLinkDialog('lytning', payment) }"
                      >
                        <Link class="h-4 w-4" />
                        Link
                      </Button>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <Pagination
          v-model:page="associationPage"
          v-model:pageSize="associationPageSize"
          :total="associationTotal"
          :loading="associationLoading"
          label="payments"
          :page-size-options="[50, 100, 200]"
          track-label="btn:PaymentPage"
          @update:pageSize="setPageSize"
        />
      </div>

      <!-- QBO tab content (Phase 25) -->
      <div v-if="activeTab === 'qbo'" class="qbo-tab">
        <Alert v-if="studio.error.value" variant="error" title="Search failed" class="page-alert">
          {{ studio.error.value }}
        </Alert>

        <div v-if="studio.results.value.length === 0 && !studio.loading.value && !studio.error.value" class="tasks-empty">
          <div class="tasks-empty-symbol">
            <RefreshCw class="h-5 w-5" />
          </div>
          <div class="tasks-empty-title">No QBO payments found</div>
          <div class="tasks-empty-copy">
            Try a different search term.
          </div>
        </div>

        <div v-else-if="studio.loading.value" class="assoc-loading">
          <RefreshCw class="h-5 w-5 spin" />
          <span>Loading QBO payments...</span>
        </div>

        <div v-else class="assoc-table-wrap">
          <table class="assoc-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Doc Number</th>
                <th>Entity</th>
                <th>Vendor</th>
                <th>Total</th>
                <th>Date</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="payment in studio.results.value" :key="payment.qboPaymentId">
                <td class="td-mono">
                  {{ payment.qboPaymentId }}
                  <Badge v-if="payment.entity === 'VendorCredit'" variant="default">VendorCredit</Badge>
                </td>
                <td>{{ payment.docNumber ?? '\u2014' }}</td>
                <td>{{ payment.entity ?? '\u2014' }}</td>
                <td>{{ payment.vendorRef?.name ?? '\u2014' }}</td>
                <td>{{ payment.totalAmt != null ? `$${payment.totalAmt.toFixed(2)}` : '\u2014' }}</td>
                <td>{{ payment.txnDate ?? '\u2014' }}</td>
                <td>
                  <Button
                    variant="ghost"
                    size="sm"
                    @click="() => { track('btn:LinkPaymentQbo', 'click'); openLinkDialog('qbo', payment) }"
                  >
                    <Link class="h-4 w-4" />
                    Link
                  </Button>
                </td>
              </tr>
            </tbody>
          </table>

          <Pagination
            :page="studio.page.value"
            :pageSize="studio.size.value"
            :total="studio.totalCount.value"
            :loading="studio.loading.value"
            label="QBO payments"
            :page-size-options="[20, 50]"
            track-label="btn:QboPaymentPage"
            @update:pageSize="studio.setSize"
          />
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.page-alert {
  margin-bottom: 1rem;
}

.entity-search {
  margin-bottom: 0.75rem;
}

.status-tabs {
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.75rem;
}

.status-tab {
  padding: 0.375rem 0.75rem;
  border-radius: var(--radius-md);
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface);
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--color-ink-soft);
  cursor: pointer;
  transition: all 0.15s;
}

.status-tab:hover {
  background: var(--color-surface-muted);
}

.status-tab--active {
  background: var(--color-ink);
  color: var(--color-surface);
  border-color: var(--color-border-strong);
}

.status-tab--disabled,
.status-tab:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.status-tab--disabled:hover,
.status-tab:disabled:hover {
  background: var(--color-surface);
}

.qbo-tab {
  margin-top: 1rem;
}

.assoc-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: center;
  padding: 2rem;
  color: var(--color-ink-soft);
}

.assoc-table-wrap {
  overflow-x: auto;
  border: 1px solid var(--color-border-soft);
  border-radius: var(--radius-md);
}

.assoc-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.assoc-table th {
  text-align: left;
  padding: 0.625rem 0.75rem;
  background: var(--color-surface-muted);
  border-bottom: 1px solid var(--color-border-soft);
  font-weight: 600;
  color: var(--color-ink);
  white-space: nowrap;
}

.assoc-table td {
  padding: 0.625rem 0.75rem;
  border-bottom: 1px solid var(--color-border-soft);
  color: var(--color-ink);
}

.assoc-table tr:last-child td {
  border-bottom: none;
}

.td-mono {
  font-family: monospace;
  font-size: 0.7rem;
}

.action-cell {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.sync-indicator {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  color: var(--color-ink-soft);
  font-size: 0.75rem;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 1s linear infinite;
}
</style>
